import { AfterContentInit, AfterViewInit, ChangeDetectorRef, Component, ContentChild, ContentChildren, ElementRef, EventEmitter, HostBinding, Input, OnChanges, OnInit, Output, QueryList, Renderer2, SimpleChanges, ViewChild } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AsyncPipe, NgForOf, NgIf } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatColumnDef, MatHeaderRow, MatTable, MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { SelectionModel } from '@angular/cdk/collections';
import { MatCardModule } from '@angular/material/card';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { BehaviorSubject, filter, merge, Observable, startWith, of as observableOf } from 'rxjs';
import { debounceTime, delay, map, switchMap, tap } from 'rxjs/operators';
import { ButtonSpacerDirective } from '../directives/button.directive';
import { AppTableDirective, AppTableRowDirective } from './table.directive';
import { ColumnService, TableColumn, TableTypeNames } from 'app/services/column.service';
import { HostListenerService } from 'app/services/host-listener.service';
import { PaginationMeta } from 'app/models/entities/pagination';
import { TableFilters, TableFiltersSelection } from 'app/models/entities/table-filters';
import { ColumnMap } from 'app/models/entities/column-map';
import { MatDividerModule } from '@angular/material/divider';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatToolbarModule } from '@angular/material/toolbar';
import { FlexSpacerDirective } from '../directives/flex-spacer.directive';
import { AnimatedDelayDirective, AnimatedDirective } from '../directives/animated.directive';
import { EmptyStateModule } from '../empty-state/empty-state.module';
import { EmptyStateComponent } from '../empty-state/empty-state.component';
import { AppLoadingTableStateComponent } from './loading-table-state/loading-table-state.component';
import { FilteringService } from '../../../../services/filtering.service';

export interface SelectedEvent<T>
{
    loading$: BehaviorSubject<boolean>;
    source: T;
}

export interface LoadedEvent<T>
{
    data: T[];
    mode: string;
    initial: boolean;
    initialEmptyState: boolean;
    loading: boolean;
}

export enum TableModes {
    DATA_ARRAY,
    DATA_SOURCE,
}

export enum TableChangeSource {
    LOAD = 'Load',
    PARAMS = 'Query Params',
    REFRESH = 'Refresh',
    SORT = 'Sort',
    PAGE = 'Page',
    SEARCH = 'Search',
    FILTER = 'Filter',
}

@UntilDestroy()
@Component({
    selector: 'app-table',
    templateUrl: './table.component.html',
    styleUrls: ['./table.component.scss'],
    standalone: true,
    imports: [
        MatCardModule,
        MatTableModule,
        AsyncPipe,
        MatProgressSpinnerModule,
        MatIconModule,
        MatProgressBarModule,
        MatPaginatorModule,
        NgIf,
        MatDividerModule,
        NgForOf,
        MatButtonModule,
        MatCheckboxModule,
        MatFormFieldModule,
        ReactiveFormsModule,
        MatInputModule,
        MatToolbarModule,
        MatMenuModule,
        FlexSpacerDirective,
        AppTableRowDirective,
        AppTableDirective,
        ButtonSpacerDirective,
        AnimatedDirective,
        AnimatedDelayDirective,
        EmptyStateModule,
        AppLoadingTableStateComponent,
    ],
})
export class AppTableComponent<T> implements OnChanges, OnInit, AfterContentInit, AfterViewInit
{
    @Input() data: T[]|MatTableDataSource<T> = [];
    @Input() type: TableTypeNames;
    @Input() columnMap: ColumnMap[] = [];
    @Input() rowSelectable = false;
    @Input() selectedCount = 0;
    @Input() hasFilters = true;
    @Input() searchControl = new FormControl<string>({ value: '', disabled: true });
    @Input() pageSizeOptions = [
        10,
        25,
        50,
        100,
    ];

    // expose MatSort as custom input to set sort for actual mat-table, not wrapper
    @Input() columnSort: MatSort;
    @Input() sortingDataAccessor: (data: T, sortHeaderId: string) => any;
    @Input() filterPredicate: (data: T, filterString: string) => boolean;

    @Output() search = new EventEmitter<string>();
    @Output() showFilters = new EventEmitter<TableFilters>();
    @Output() rowSelect = new EventEmitter<SelectedEvent<T>>();
    @Output() loaded = new EventEmitter<LoadedEvent<T>>();

    @ViewChild(MatTable, {static: true}) table: MatTable<T>;
    @ViewChild(MatTable, {read: ElementRef}) tableRef: ElementRef;
    @ViewChild(MatHeaderRow, {read: ElementRef}) tableHeaderRef: ElementRef;
    @ViewChild(MatPaginator) paginator: MatPaginator;

    @ContentChild(EmptyStateComponent) emptyStateRef!: EmptyStateComponent;
    @ContentChildren(MatColumnDef) columnDefs!: QueryList<MatColumnDef>;

    selectedColumns = new SelectionModel<TableColumn>(true, []);
    resultsLength: number;
    isLoadingInitialResults = true;
    isLoadingResults = true;
    initialEmptyState = false;
    rowsAnimated = true;

    readonly loadingRow$: { [prop: string]: BehaviorSubject<boolean>; } = {};
    readonly loadingState$ = new BehaviorSubject<boolean>(true);
    readonly data$ = new BehaviorSubject<T[] | { data: T[]; meta: PaginationMeta }>(null);

    private mode: TableModes = TableModes.DATA_SOURCE;
    private initialResultsLength = -1;

    private readonly src = TableChangeSource;
    private readonly tableStateChange$ = new BehaviorSubject<TableChangeSource>(this.src.LOAD);

    private readonly filtersState$ = new BehaviorSubject<TableFiltersSelection>(new TableFiltersSelection({}));
    private readonly searchState$ = new BehaviorSubject<string>('');
    private readonly refresh$ = new BehaviorSubject<void>(null);

    constructor(
            private _activatedRoute: ActivatedRoute,
            private _filteringService: FilteringService,
            private _host: HostListenerService,
            private _renderer: Renderer2,
            private _cd: ChangeDetectorRef,
    ) {
    }

    /** explicitly set classes for empty states: on initial load vs. after search/filter */
    @HostBinding('class.empty-table-onload') get addInitialEmptyStateClass() {
        return this.initialEmptyState;
    }

    @HostBinding('class.empty-table-onchange') get addChangeEmptyStateClass() {
        return !this.initialEmptyState && this.resultsLength === 0;
    }

    get hasData(): boolean {
        return !this.initialEmptyState && this.resultsLength > 0;
    }

    get displayedColumns(): ColumnMap[] {
        return this.columnMap.filter((column) => {
            return this.selectedColumns.isSelected(column);
        });
    }

    get displayedColumnKeys$() {
        return this._host.isMobileMode().pipe(map((isMobile) => {
            let columns: string[];
            if (isMobile) {
                columns = this.displayedColumns.filter((column) => !!column.isMobile).map((column) => column.key);
            } else {
                columns = this.displayedColumns.filter((column) => !column.isMobile).map((column) => column.key);
            }

            if (this.rowSelectable && !isMobile) {
                columns.push('arrowRight');
            }

            return columns;
        }));
    }

    get mobileMode$() {
        return this._host.isMobileMode();
    }

    get tabletMode$() {
        return this._host.isTabletMode();
    }

    get tableState$() {
        return this.tableStateChange$.asObservable().pipe(
                debounceTime(150),
                map((src) => {
                    return { data: this.data, event: src };
                }),
        );
    }

    get connectTable$() {
        this.mode = TableModes.DATA_ARRAY;
        return this._connect$;
    }

    get connectDataTable$() {
        this.mode = TableModes.DATA_SOURCE;
        return this._connect$;
    }

    get availableColumns() {
        return this.columnMap.filter((column) => {
            if (!column.title) {
                return false;
            }

            if (column.disabled) {
                return !column.disabled();
            }

            return true;
        });
    }

    get searchValue() {
        return this.searchState$.getValue();
    }

    get filters() {
        return this.filtersState$.getValue().filters;
    }

    set filters(filters: TableFilters) {
        const selection = this.filtersState$.getValue();
        selection.add(filters);

        this.filtersState$.next(selection);
    }

    set loading(isLoading: boolean) {
        const loaded: LoadedEvent<T> = {
            data: Array.isArray(this.data) ? this.data : this._dataAsMatTableDataSource.data,
            mode: TableModes[this.mode],
            initial: this.isLoadingInitialResults && !isLoading,
            initialEmptyState: !isLoading && this.initialResultsLength === 0,
            loading: !this.isLoadingResults,
        };

        if (loaded.initial) {
            this.isLoadingInitialResults = false;

            if (loaded.data.length > 0) {
                this.searchControl.enable({ emitEvent: false });
            }

            if (this.mode === TableModes.DATA_ARRAY) {
                this._attachMatTableDataSourceHandling(this._dataAsMatTableDataSource);
            }
        }
        this.initialEmptyState = loaded.initialEmptyState;

        this.isLoadingResults = isLoading;
        this.loaded.emit(loaded);
    }

    private get _connect$() {
        // by default, table only updates data for initial load and forced refresh
        let source$: Observable<any> = merge(this.refresh$.pipe(tap(() => this.tableStateChange$.next(this.src.REFRESH))));

        if (this.mode === TableModes.DATA_SOURCE) {
            // for DATA_SOURCE mode, listen to all changes to trigger fetch from api
            source$ = merge(
                    this._activatedRoute.queryParams.pipe(tap(() => this.tableStateChange$.next(this.src.PARAMS))),
                    this.columnSort?.sortChange.pipe(tap(() => this.tableStateChange$.next(this.src.SORT))),
                    this.paginator.page.pipe(tap(() => this.tableStateChange$.next(this.src.PAGE))),
                    this.searchState$.pipe(tap(() => this.tableStateChange$.next(this.src.SEARCH))),
                    this.filtersState$.pipe(tap(() => this.tableStateChange$.next(this.src.FILTER))),
                    this.refresh$.pipe(tap(() => this.tableStateChange$.next(this.src.REFRESH))),
            );
        }

        return source$.pipe(
                filter(() => !this.isLoadingInitialResults),
                map(() => {
                    if (!this.isLoadingInitialResults) {
                        this.rowsAnimated = false;
                    }

                    this.loading = true;
                }),
                debounceTime(500),
                startWith({}),
        );
    }

    private get _dataAsMatTableDataSource() {
        return this.data as MatTableDataSource<T>;
    }

    addFilter(filters: TableFilters) {
        this.filtersState$.next(this.filtersState$.getValue().add(filters));
    }

    removeFilter(key: string) {
        this.filtersState$.next(this.filtersState$.getValue().remove(key));
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes && changes.hasOwnProperty('columnMap')) {
            const keys = ColumnService.read(this.type);
            if (keys) {
                const values = this.columnMap.filter((column) => {
                    return keys.indexOf(column.key) > -1;
                });

                this.selectedColumns.select(...values);
            } else {
                this.resetColumns();
            }
        }
    }

    ngOnInit() {
        this._activatedRoute.queryParams.pipe(filter((params) => !!params), untilDestroyed(this)).subscribe((params) => {
            this.filters = params;
        });

        this.data$.asObservable().pipe(
                filter((res) => !!res),
                delay(150),
                untilDestroyed(this),
        ).subscribe((res: T[] | { data: T[], meta: PaginationMeta }) => {
            if (Array.isArray(res)) {
                this.data = this.mode === TableModes.DATA_ARRAY ? new MatTableDataSource<T>(res) : res;
                this.resultsLength = res.length;
            } else if (res.hasOwnProperty('data') && res.hasOwnProperty('meta')) {
                this.data = res.data;
                this.resultsLength = res.meta.total;
            } else {
                console.warn('Invalid Data source!');
                this.resultsLength = 0;
                this.data = [];
            }

            // track totals for initial load vs. subsequent loads (e.g. after filter or sort)
            if (this.initialResultsLength === -1) {
                this.initialResultsLength = this.resultsLength;
            }

            // check against empty state for min-width
            this._setMinWidthForTable();

            this.loading = false;
        });
    }

    ngAfterContentInit() {
        this.columnDefs.forEach(columnDef => this.table.addColumnDef(columnDef));
        this._cd.detectChanges();
    }

    ngAfterViewInit() {
        // initial load of table, set min-width used
        this._setMinWidthForTable();

        this.columnSort?.sortChange.pipe(untilDestroyed(this)).subscribe({
            next: () => {
                if (this.mode !== TableModes.DATA_ARRAY) {
                    this.paginator.pageIndex = 0;
                }
            },
        });

        this.searchControl.valueChanges.pipe(
                debounceTime(500),
                switchMap((value) => {
                    this.searchState$.next(value);

                    if (value.trim().length > 0) {
                        this.emptyStateRef.searchTerm = value;
                    }

                    if (this.mode === TableModes.DATA_ARRAY) {
                        this._dataAsMatTableDataSource.filter = value.trim().toLowerCase();
                        return observableOf(value).pipe(delay(500));
                    }
                    return observableOf(value);
                }),
                untilDestroyed(this),
        ).subscribe({
            next: (value) => {
                this.paginator.pageIndex = 0;

                if (value.trim() === '') {
                    this.emptyStateRef.searchTerm = '';
                }
            },
        });
    }

    selectRow(row: T) {
        if (this.rowSelectable) {
            this.loadingRow$[row['id']] = new BehaviorSubject<boolean>(true);

            this.rowSelect.emit({
                loading$: this.loadingRow$[row['id']], source: row,
            });
        }
    }

    refresh() {
        this.initialResultsLength = -1;
        this.initialEmptyState = false;

        this.refresh$.next();
    }

    resetColumns() {
        const values = this.columnMap.filter((column) => {
            if (column.defaultShow) {
                return !!column.defaultShow();
            }

            return true;
        });

        this.selectedColumns.select(...values);
        ColumnService.clear(this.type);
        this._setMinWidthForTable();
    }

    storeColumns() {
        ColumnService.store(this.type, this.selectedColumns.selected);
        this._setMinWidthForTable();
    }

    readonly defaultSorting = (data: T, sortHeaderId: string) => {
        return this._filteringService.normalizeMixedDataValueForSorting(this._filteringService.extractPath(sortHeaderId, data));
    };

    // Attaching basic handling for sorting, filtering, pagination on the clientside
    // It is strongly recommended to use a custom datasource (or api call) for more advanced functionality
    private _attachMatTableDataSourceHandling(dataSource: MatTableDataSource<T>) {
        if (this.mode !== TableModes.DATA_ARRAY) {
            console.warn('This table expects sorting, pagination, and filtering to be done server-side');
            return;
        }

        // mat-paginator
        dataSource.paginator = this.paginator;

        // mat-sort
        dataSource.sort = this.columnSort;

        // fallback to default sorting if not set for specific table
        dataSource.sortingDataAccessor = this.sortingDataAccessor || this.defaultSorting;

        // search & table filters
        // TODO: TableFilters (though this may cause a table refresh to occur first)
        const originalFilterFunction = dataSource.filterPredicate;
        const defaultFilter = (data: T, filterString: string) => {
            // a few "common" use cases currently in use for tables
            if (data) {
                const item = data as any;
                const parts: { [prop: string]: any } = {};

                if (item.name) {
                    parts.name = item.name;
                }

                if (item.title) {
                    parts.title = item.title;
                }

                if (item.email) {
                    parts.email = item.email;
                }

                return originalFilterFunction(parts as any, filterString);
            }

            return false;
        };
        dataSource.filterPredicate = this.filterPredicate || defaultFilter;

        // no loading/loaded animations needed for rows with already loaded clientside
        this.rowsAnimated = false;
    }

    private _setMinWidthForTable() {
        if (this.tableRef) {
            // first column width should be minimum 24rem/384px
            // TODO: fix how with gets dynamically set
            const minWidth = '1200px'; // '384px'; // `${384 + (150 * this.selectedColumns.selected.length - 1)}px`;

            // dynamically set min-width based on known columns vs. arbitrary css
            this._renderer.setStyle(this.tableRef.nativeElement, 'min-width', minWidth);
        }
    }
}
